/**
 * Automatic offline "top up".
 *
 * Every time the app opens with a connection, quietly check the files this
 * build needs against what is already stored on the device and pull down
 * anything missing, so a phone that has been sitting in a truck for weeks is
 * still complete before it goes into a basement with no signal.
 *
 * Deliberately silent: no UI, low concurrency, throttled, and it never blocks
 * startup. The manual "Download all files" button in Settings stays the
 * user-facing way to force a full re-pull.
 */
const STAMP_KEY = "tt-offline-topup-at";
const MIN_GAP_MS = 6 * 60 * 60 * 1000; // at most once every 6 hours
const CONCURRENCY = 4;

function recentlyRan(): boolean {
  try {
    const raw = localStorage.getItem(STAMP_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < MIN_GAP_MS;
  } catch {
    return false;
  }
}

function stamp() {
  try {
    localStorage.setItem(STAMP_KEY, String(Date.now()));
  } catch {
    /* storage full — retry next launch */
  }
}

async function missingFiles(): Promise<string[]> {
  const res = await fetch("/offline-manifest.json", { cache: "no-store" });
  if (!res.ok) return [];
  const data = (await res.json()) as { files?: string[] };
  const urls = (data.files ?? []).map((f) => new URL(f, window.location.origin).href);

  const missing: string[] = [];
  for (const url of urls) {
    try {
      if (!(await caches.match(url, { ignoreSearch: true }))) missing.push(url);
    } catch {
      /* ignore a single bad entry */
    }
  }
  return missing;
}

async function pull(urls: string[]) {
  let i = 0;
  const worker = async () => {
    while (i < urls.length) {
      const url = urls[i++];
      try {
        // Goes through the service worker, which stores it for offline use.
        await fetch(url, { cache: "reload" });
      } catch {
        /* one failure shouldn't abort the top-up */
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker));
}

async function topUp() {
  if (!navigator.onLine) return;
  if (recentlyRan()) return;
  try {
    const missing = await missingFiles();
    if (missing.length) await pull(missing);
    stamp();
  } catch {
    /* offline or manifest unavailable — try again next launch */
  }
}

export function startOfflineTopUp() {
  if (typeof window === "undefined") return;
  if (!("caches" in window) || !("serviceWorker" in navigator)) return;
  if (!import.meta.env.PROD) return;
  // Native builds ship every file inside the app bundle already.
  if ((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) return;

  const run = () => void topUp();

  // Wait for the worker to control the page, and stay out of the way of the
  // first paint.
  navigator.serviceWorker.ready
    .then(() => window.setTimeout(run, 4000))
    .catch(() => undefined);

  // Reconnected after being offline, or brought back from the background.
  window.addEventListener("online", run);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
}
